import { BorrowerSeperator } from 'pages/LoanApplication/config'

const CoBorrowers = Object.values(BorrowerSeperator).filter((s) => s !== BorrowerSeperator.BORROWER)

/** Stored on fields; includes legacy `boolean` for all co-borrower slots. */
export type CoborrowerRequiredConfig = boolean | Record<BorrowerSeperator, boolean> | undefined

export function normalizeCoborrowerRequiredRecord(
  coborrowerRequired: CoborrowerRequiredConfig,
  required: boolean,
): Record<BorrowerSeperator, boolean> {
  const result = {} as Record<BorrowerSeperator, boolean>
  for (const sep of Object.values(BorrowerSeperator)) {
    result[sep] = required
  }
  if (coborrowerRequired === undefined) {
    return result
  }
  if (typeof coborrowerRequired === 'boolean') {
    result[BorrowerSeperator.BORROWER] = required
    for (const sep of CoBorrowers) {
      result[sep] = coborrowerRequired
    }
    return result
  }
  for (const sep of Object.values(BorrowerSeperator)) {
    result[sep] =
      coborrowerRequired[sep] !== undefined ? coborrowerRequired[sep]! : required
  }
  return result
}

/**
 * Resolves whether a field is required for a given borrower slot, honoring legacy
 * `coborrowerRequired: boolean` and per-borrower `Record` values. Returns `undefined` when
 * the admin config does not define any rule that should override the screen default.
 */
export function getEffectiveFieldRequiredForBorrower(
  borrowerSeperator: BorrowerSeperator,
  field: { required?: boolean; coborrowerRequired?: CoborrowerRequiredConfig },
): boolean | undefined {
  const { required, coborrowerRequired: co } = field
  if (co === undefined && required === undefined) {
    return undefined
  }
  if (borrowerSeperator === BorrowerSeperator.BORROWER) {
    if (required !== undefined) {
      return required
    }
    if (co !== undefined && typeof co === 'object' && co[BorrowerSeperator.BORROWER] !== undefined) {
      return co[BorrowerSeperator.BORROWER]
    }
    return undefined
  }
  if (co !== undefined) {
    if (typeof co === 'boolean') {
      return co
    }
    if (typeof co === 'object' && co[borrowerSeperator] !== undefined) {
      return co[borrowerSeperator]
    }
  }
  if (required !== undefined) {
    return required
  }
  return undefined
}

/** True if any co-borrower slot (not primary) is required for this field. */
export function hasAnyCoBorrowerSlotRequired(field: {
  required?: boolean
  coborrowerRequired?: CoborrowerRequiredConfig
}): boolean {
  return CoBorrowers.some(
    (sep) => getEffectiveFieldRequiredForBorrower(sep, field) === true,
  )
}

/** True if any of the eight borrower positions (1–8) is required for this field. */
export function hasAnyBorrowerRequired(field: {
  required?: boolean
  coborrowerRequired?: CoborrowerRequiredConfig
}): boolean {
  return Object.values(BorrowerSeperator).some(
    (sep) => getEffectiveFieldRequiredForBorrower(sep, field) === true,
  )
}
